import styled from "@emotion/styled";
import { InputAdornment, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MagnifyingGlass, Receipt } from "@phosphor-icons/react";
import { Button, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import { useExpensesPaginated, useExportExpenses } from "features/finances";
import { capitalize, replace } from "lodash";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import { useAclStore } from "stores/aclStore";
import { EthiopianDateField } from "../../components/EthiopianDateField";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

// const RevenueSettingButton = styled.button`
//   outline: none;
//   border: none;
//   background: none;
//   color: #789;
//   display: flex;
//   align-items: center;
//   gap: 4px;
//   cursor: pointer;
//   font-weight: 500;
// `;

const RequisitionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Status = styled.div`
  border-radius: 2px;
  background: ${({ color }) => color};
  color: #ffffff;
  padding: 4px 8px;
  text-transform: capitalize;
  cursor: pointer;
`;

export const ExpenseTable = ({ onEdit }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });

  const navigate = useNavigate();
  // const openRecordExpenseModal = useExpenseStore(
  //   state => state.openRecordExpenseModal
  // );
  const role = useAclStore(state => state.role);
  // const openUpdateExpenseModal = useExpenseStore(
  //   state => state.openUpdateExpenseModal
  // );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const expensesQuery = useExpensesPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    queries: {
      search: debouncedSearch,
      startDate: filterDates.startDate,
      endDate: filterDates.endDate
    }
  });

  const { mutate: exportExpenses, isLoading: isExporting } =
    useExportExpenses();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const statusColors = {
    advanced: "#f4ca10",
    on_approval: "#2BBDC5",
    settled: "#50C50D"
  };

  const expenseStatuses = {
    advanced: "Advance",
    on_approval: "On Approval",
    settled: "Settled"
  };

  const columns = [
    {
      field: "id",
      headerName: "Expense ID",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "14px" }}>
          {row.requisition_record?.transaction_number ||
            row.requisition?.transaction_number}
        </Typography>
      )
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      renderCell: ({ row }) => (
        <>{Number(row.advance_to_clear || 0).toLocaleString()}</>
      )
    },
    {
      field: "chequeNumber",
      headerName: "Cheque Number",
      flex: 1,
      renderCell: ({ row }) => (
        <>
          {row.requisition_record?.check_number ||
            row.requisition?.cheque_number}
        </>
      )
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      renderCell: ({ row }) => (
        <>{row.requisition_record?.reason || row.requisition?.reason}</>
      )
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      renderCell: ({ row }) => (
        <>{row.requisition_record?.team?.name || row.requisition?.team?.name}</>
      )
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: ({ row }) => (
        <>{moment(row.created_at).format("DD-MM-YYYY")}</>
      )
    },
    {
      field: "expense_status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Status
            color={statusColors[row.expense_status.toLowerCase()]}
            onClick={e => {
              e.stopPropagation();

              const isAccountant = role?.toLowerCase() === "accountant";

              if (
                isAccountant &&
                row.expense_status.toLowerCase() === "on_approval"
              ) {
                navigate(
                  `/finances/temp/expense/${row.id}/settlement/approval`
                );
              } else {
                navigate(`/finances/temp/expense/${row.id}/settlement`);
              }
            }}
          >
            {expenseStatuses[row.expense_status.toLowerCase()] ||
              replace(capitalize(row.expense_status), "_", " ")}
          </Status>
        );
      }
    }
  ];

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  return (
    <Container>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Title>Expense Table</Title>
          <PrimaryActions>
            <RequisitionButton
              onClick={() => navigate("/finances/temp/expense/requisition")}
            >
              <Receipt size={16} />
              <span>Requisition</span>
            </RequisitionButton>
            {/* <RevenueSettingButton
              onClick={() => navigate("/finances/temp/expense/settings")}
            >
              <GearSix size={16} />
              <span>Expense Setting</span>
            </RevenueSettingButton> */}
            {/* <Button onClick={openRecordExpenseModal}>Record Expense</Button> */}
          </PrimaryActions>
        </Header>
        <SecondaryActions>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EthiopianDateField
              size="small"
              label="Start date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <EthiopianDateField
              size="small"
              label="End date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
            <Button onClick={handleFilter}>Filter</Button>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              neutral={1}
              placeholder="Search expense"
              register={() => {}}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={16} />
                  </InputAdornment>
                )
              }}
              autoComplete="off"
              onChange={e => setSearch(e.target.value)}
            />
            <Button
              onClick={() =>
                exportExpenses({
                  churchId: selectedChurch?.id,
                  queries: {
                    search: debouncedSearch,
                    startDate: filterDates.startDate,
                    endDate: filterDates.endDate
                  }
                })
              }
              disabled={isExporting}
            >
              Export
            </Button>
          </Stack>
        </SecondaryActions>
      </Stack>
      <DataGrid
        autoHeight
        rows={expensesQuery.data?.result?.data || []}
        rowCount={expensesQuery.data?.result?.total || 0}
        loading={expensesQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
        onRowClick={({ row }) => {
          onEdit(row);
          // openUpdateExpenseModal();
        }}
      />
    </Container>
  );
};
